import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "sonner";
import { AppProvider, useApp } from "./context/AppContext";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import HotelDetail from "./pages/HotelDetail";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Track from "./pages/Track";
import MapView from "./pages/MapView";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Profile from "./pages/Profile";
import { CustomerAuthProvider } from "./context/CustomerAuthContext";
import ScrollToTopOnRoute from "./components/ScrollToTopOnRoute";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminHotels from "./pages/admin/AdminHotels";
import AdminReservations from "./pages/admin/AdminReservations";
import AdminReviews from "./pages/admin/AdminReviews";
import AdminCustomers from "./pages/admin/AdminCustomers";
import AdminOccupancy from "./pages/admin/AdminOccupancy";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminTransfers from "./pages/admin/AdminTransfers";
import AdminGuides from "./pages/admin/AdminGuides";
import AdminGuideRequests from "./pages/admin/AdminGuideRequests";
import AdminPromoCodes from "./pages/admin/AdminPromoCodes";
import AdminCompanies from "./pages/admin/AdminCompanies";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import Transfers from "./pages/Transfers";
import Guides from "./pages/Guides";
import Flights from "./pages/Flights";
import Visa from "./pages/Visa";
import Hajj from "./pages/Hajj";
import RealEstate from "./pages/RealEstate";
import AdminServiceRequests from "./pages/admin/AdminServiceRequests";
import AdminProperties from "./pages/admin/AdminProperties";
import AdminHajj from "./pages/admin/AdminHajj";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";

const RequireAdmin = ({ children }) => {
    const { admin } = useApp();
    const token = localStorage.getItem("hs_admin_token");
    if (!token) return <Navigate to="/admin/login" replace />;
    if (admin === null && !token) return <Navigate to="/admin/login" replace />;
    return children;
};

const AnimatedRoutes = () => {
    const location = useLocation();
    return (
        <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
                <Route element={<Layout />}>
                    <Route path="/" element={<Home />} />
                    <Route path="/hotels/:id" element={<HotelDetail />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/track" element={<Track />} />
                    <Route path="/map" element={<MapView />} />
                    <Route path="/transfers" element={<Transfers />} />
                    <Route path="/guides" element={<Guides />} />
                    <Route path="/flights" element={<Flights />} />
                    <Route path="/visa" element={<Visa />} />
                    <Route path="/hac" element={<Hajj />} />
                    <Route path="/emlak" element={<RealEstate />} />
                    <Route path="/payment/success" element={<PaymentSuccess />} />
                    <Route path="/payment/cancel" element={<PaymentCancel />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Route>
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route
                    path="/admin"
                    element={
                        <RequireAdmin>
                            <AdminLayout />
                        </RequireAdmin>
                    }
                >
                    <Route index element={<AdminDashboard />} />
                    <Route path="hotels" element={<AdminHotels />} />
                    <Route path="reservations" element={<AdminReservations />} />
                    <Route path="reviews" element={<AdminReviews />} />
                    <Route path="customers" element={<AdminCustomers />} />
                    <Route path="transfers" element={<AdminTransfers />} />
                    <Route path="guides" element={<AdminGuides />} />
                    <Route path="guide-requests" element={<AdminGuideRequests />} />
                    <Route path="service-requests" element={<AdminServiceRequests />} />
                    <Route path="properties" element={<AdminProperties />} />
                    <Route path="hajj" element={<AdminHajj />} />
                    <Route path="occupancy" element={<AdminOccupancy />} />
                    <Route path="promo-codes" element={<AdminPromoCodes />} />
                    <Route path="companies" element={<AdminCompanies />} />
                    <Route path="analytics" element={<AdminAnalytics />} />
                    <Route path="settings" element={<AdminSettings />} />
                </Route>
            </Routes>
        </AnimatePresence>
    );
};

function App() {
    return (
        <AppProvider>
            <CustomerAuthProvider>
                <BrowserRouter>
                    <ScrollToTopOnRoute />
                    <Toaster
                        position="top-right"
                        toastOptions={{
                            style: { background: "#082c27", color: "#fdfbf7", border: "1px solid #14534a" },
                        }}
                    />
                    <AnimatedRoutes />
                </BrowserRouter>
            </CustomerAuthProvider>
        </AppProvider>
    );
}

export default App;
